<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';
import CrmDataTable from '../../../crm/components/reports/CrmDataTable.vue';
import AppSmartLoader from '../../../crm/components/AppSmartLoader.vue';
import MarketingCostFormModal from '../../../crm/components/reports/marketing/MarketingCostFormModal.vue';

const store = useStore();

const showFormModal = ref(false);
const editingCost = ref(null);
const confirmDeleteId = ref(null);

const uiFlags = computed(() => store.getters['marketingCosts/getUIFlags']);
const records = computed(
  () => store.getters['marketingCosts/getMarketingCosts']
);
const suggestions = computed(
  () => store.getters['marketingCosts/getSuggestions']
);

const fetchAll = () => store.dispatch('marketingCosts/get', {});

onMounted(() => {
  fetchAll();
  store.dispatch('marketingCosts/fetchSuggestions');
});

const PLATFORM_LABELS = {
  google_ads: 'Google Ads',
  meta_ads: 'Meta Ads',
  tiktok_ads: 'TikTok Ads',
  linkedin_ads: 'LinkedIn Ads',
  twitter_ads: 'Twitter/X Ads',
  pinterest_ads: 'Pinterest Ads',
  email: 'Email',
  broadcast: 'Broadcast',
  evento: 'Evento',
  offline: 'Offline',
  outro: 'Outro',
};
const platformLabel = key => PLATFORM_LABELS[key] || key || '-';

const tableColumns = [
  { key: 'name', label: 'Nome', align: 'left' },
  { key: 'platform_label', label: 'Plataforma', align: 'left' },
  { key: 'utm_campaign', label: 'Campanha UTM', align: 'left' },
  { key: 'period_label', label: 'Período', align: 'left' },
  { key: 'cost', label: 'Custo', align: 'right', format: 'currency' },
  { key: 'actions', label: '', align: 'right' },
];

const tableRows = computed(() =>
  records.value.map(r => ({
    ...r,
    platform_label: platformLabel(r.platform),
    period_label: r.start_date
      ? `${r.start_date}${r.end_date ? ' ~ ' + r.end_date : ' ~ atual'}`
      : '-',
  }))
);

const openCreate = () => {
  editingCost.value = null;
  showFormModal.value = true;
};

const openEdit = row => {
  editingCost.value = records.value.find(r => r.id === row.id) || row;
  showFormModal.value = true;
};

const onSave = async formData => {
  if (editingCost.value) {
    await store.dispatch('marketingCosts/update', {
      id: editingCost.value.id,
      ...formData,
    });
  } else {
    await store.dispatch('marketingCosts/create', formData);
  }
  showFormModal.value = false;
  fetchAll();
};

const onDelete = async row => {
  if (confirmDeleteId.value === row.id) {
    await store.dispatch('marketingCosts/delete', row.id);
    confirmDeleteId.value = null;
    fetchAll();
  } else {
    confirmDeleteId.value = row.id;
    setTimeout(() => {
      confirmDeleteId.value = null;
    }, 3000);
  }
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div class="flex flex-col w-full max-w-[60rem] gap-6 mx-auto">
    <div class="flex items-start justify-between gap-4">
      <div class="min-w-0">
        <h1 class="text-xl font-medium text-n-slate-12">Custos manuais</h1>
        <p class="mt-1 text-sm text-n-slate-10">
          Lance aqui os custos SEM API (evento, offline, mídia impressa…).
          Google/Meta coletados automaticamente não precisam ser lançados. Tudo
          aparece cruzado em Relatórios → Marketing → Performance.
        </p>
      </div>
      <NextButton
        icon="i-lucide-circle-plus"
        label="Adicionar custo"
        sm
        class="shrink-0"
        @click="openCreate"
      />
    </div>

    <div
      v-if="uiFlags.isFetching && !records.length"
      class="flex items-center justify-center h-64"
    >
      <AppSmartLoader animation="radar" :size="48" />
    </div>

    <div
      v-else-if="!records.length"
      class="flex flex-col items-center justify-center gap-3 p-10 rounded-xl text-n-slate-11 bg-n-alpha-black2"
    >
      <span class="i-lucide-receipt size-10 text-n-slate-8" />
      <span class="text-sm">Nenhum custo manual cadastrado.</span>
      <NextButton
        icon="i-lucide-circle-plus"
        label="Adicionar o primeiro"
        sm
        @click="openCreate"
      />
    </div>

    <CrmDataTable
      v-else
      :columns="tableColumns"
      :rows="tableRows"
      export-filename="custos_marketing"
    >
      <template #actions="{ row }">
        <div class="flex items-center justify-end gap-1">
          <button
            type="button"
            class="flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors text-n-slate-10 hover:text-woot-500 hover:bg-n-alpha-1/50"
            @click="openEdit(row)"
          >
            <span class="i-lucide-pencil size-3" />
            Editar
          </button>
          <button
            type="button"
            class="flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors"
            :class="
              confirmDeleteId === row.id
                ? 'text-white bg-red-500'
                : 'text-n-slate-10 hover:text-red-500 hover:bg-red-500/10'
            "
            @click="onDelete(row)"
          >
            <span class="i-lucide-trash-2 size-3" />
            {{ confirmDeleteId === row.id ? 'Confirmar?' : 'Excluir' }}
          </button>
        </div>
      </template>
    </CrmDataTable>

    <MarketingCostFormModal
      v-if="showFormModal"
      :editing="editingCost"
      :suggestions="suggestions"
      @save="onSave"
      @close="showFormModal = false"
    />
  </div>
</template>
